import { useState, useEffect, useRef, useMemo, ReactNode } from "react";
import { Plus, Minus, Search, ShieldCheck, CalendarCheck, BadgeDollarSign, GraduationCap } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add("visible"); observer.unobserve(el); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

type Category = "all" | "trust" | "sessions" | "pricing" | "learning";

const categories: { id: Category; label: string; icon: React.ReactNode }[] = [
  { id: "all", label: "All questions", icon: null },
  { id: "trust", label: "Safety & Trust", icon: <ShieldCheck className="w-3.5 h-3.5" /> },
  { id: "sessions", label: "Sessions & Booking", icon: <CalendarCheck className="w-3.5 h-3.5" /> },
  { id: "pricing", label: "Pricing", icon: <BadgeDollarSign className="w-3.5 h-3.5" /> },
  { id: "learning", label: "Learning", icon: <GraduationCap className="w-3.5 h-3.5" /> },
];

const faqs: { q: string; a: string | ReactNode; category: Category }[] = [
  {
    category: "trust",
    q: "How do I know a teacher is qualified?",
    a: "Every teacher goes through a multi-step verification process — we check academic qualifications, professional experience, and conduct a background review before they're listed. You'll see a 'Verified' badge on all approved profiles, and you can view their credentials and student reviews directly on their profile page.",
  },
  {
    category: "trust",
    q: "Are sessions safe and private?",
    a: "Bookmycoaching does not sharing of personal contact details between students and teachers. Parents can join any session at any time, and all session activity is logged for safeguarding purposes.",
  },
  {
    category: "trust",
    q: "What if my child doesn't connect with the teacher?",
    a: "Finding the right fit matters more than anything else. Bookmycoaching will help you find a teacher who can help you achieve your goals. You can switch teachers at any time.",
  },
  {
    category: "sessions",
    q: "Can I sit in on my child's sessions?",
    a: "Yes, absolutely. Parents are always welcome to join any session. You'll receive the session link alongside your child and can drop in or stay for the whole lesson.",
  },
  {
    category: "sessions",
    q: "Can I book a trial session before committing?",
    a: "Most teachers offer an introductory session at a reduced rate so you can experience their teaching style before purchasing a package. Check the teacher's profile page for trial session availability.",
  },
  {
    category: "sessions",
    q: "What happens if a session is cancelled last minute?",
    a: "If a teacher cancels without sufficient notice, you'll receive a full refund or credit to reschedule — automatically, with no chasing required. Our cancellation policy protects both students and teachers, and full details are shown at booking.",
  },
  {
    category: "pricing",
    q: "How does pricing work? Are there hidden fees?",
    a: "Prices are set by each teacher and displayed upfront on their profile — per session or as a discounted package. There are no booking fees, platform surcharges, or hidden charges. You pay exactly what you see.",
  },
  {
    category: "pricing",
    q: "What are teacher subscription tiers?",
    a: (
      <>
        Teachers can subscribe to unlock additional features like the ability to offer group classes, increased visibility in search results, and bonus monthly credits. We offer flexible subscription tiers — from Silver through Platinum — to help teachers reach more students. For current pricing and tier details, visit our{" "}
        <Link to="/teacher-subscription" className="text-[hsl(237,52%,50%)] font-semibold hover:underline">
          teacher subscription page
        </Link>
        .
      </>
    ),
  },
  {
    category: "pricing",
    q: "Do I need to pay more to book a subscribed teacher?",
    a: (
      <>
        No. Teacher subscription is separate from session pricing. Whether a teacher is subscribed or not, you pay exactly what they've set for their sessions. Subscriptions simply let teachers offer more types of classes and appear more often in search. Learn more on our{" "}
        <Link to="/teacher-subscription" className="text-[hsl(237,52%,50%)] font-semibold hover:underline">
          teacher subscription page
        </Link>
        .
      </>
    ),
  },
  {
    category: "learning",
    q: "What subjects and age groups are available?",
    a: "We have teachers across 50+ subjects — from primary school core subjects to advanced university-level topics, creative arts, coding, music, and professional skills. Use the subject grid or search bar to find what you need.",
  },
  {
    category: "learning",
    q: "Is online teaching as effective as in-person?",
    a: "Research consistently shows that one-to-one online teaching, done well, produces results equal to or better than in-person tutoring. Our teachers are trained for online delivery, and the platform includes shared whiteboards, session notes, and homework tracking to make every session count.",
  },
  {
    category: "learning",
    q: "How often should my child have sessions?",
    a: "This depends on your goals. Most students benefit from 1–2 sessions per week for steady progress, while exam-focused students often book more intensively in the lead-up to tests. Your teacher will recommend a cadence after the first session.",
  },
];

function FAQItem({ q, a, isOpen, onToggle }: { q: string; a: string | ReactNode; isOpen: boolean; onToggle: () => void }) {
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    if (isOpen) {
      el.style.maxHeight = el.scrollHeight + "px";
      el.style.opacity = "1";
    } else {
      el.style.maxHeight = "0px";
      el.style.opacity = "0";
    }
  }, [isOpen]);

  return (
    <div className={cn(
      "rounded-2xl border transition-all duration-300",
      isOpen
        ? "border-[hsl(237,52%,50%)]/30 bg-[hsl(237,52%,50%)]/5 shadow-sm"
        : "border-border/60 bg-card hover:border-border hover:shadow-sm"
    )}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
        aria-expanded={isOpen}
      >
        <span className={cn(
          "text-sm font-semibold leading-snug transition-colors",
          isOpen ? "text-[hsl(237,52%,40%)]" : "text-foreground"
        )}>
          {q}
        </span>
        <div className={cn(
          "w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200",
          isOpen ? "bg-[hsl(237,52%,50%)] text-white rotate-0" : "bg-muted text-muted-foreground"
        )}>
          {isOpen ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
        </div>
      </button>
      <div
        ref={bodyRef}
        style={{ maxHeight: "0px", opacity: "0", overflow: "hidden", transition: "max-height 0.3s ease, opacity 0.25s ease" }}
      >
        <div className="px-6 pb-5">
          <p className="text-sm text-muted-foreground leading-relaxed">{a}</p>
        </div>
      </div>
    </div>
  );
}

export function FAQSection() {
  const headerRef = useReveal<HTMLDivElement>();
  const listRef = useReveal<HTMLDivElement>();

  const [activeCategory, setActiveCategory] = useState<Category>("all");
  const [search, setSearch] = useState("");
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return faqs.filter((f) => {
      const matchesCategory = activeCategory === "all" || f.category === activeCategory;
      const matchesSearch = !q || f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, search]);

  // Reset open item when filter changes
  useEffect(() => { setOpenIndex(filtered.length > 0 ? 0 : null); }, [activeCategory, search]);

  const half = Math.ceil(filtered.length / 2);
  const left = filtered.slice(0, half);
  const right = filtered.slice(half);

  return (
    <section className="py-24 bg-background relative overflow-hidden">
      <div className="absolute -bottom-20 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[hsl(237,52%,82%)]/20 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">

        {/* Header */}
        <div ref={headerRef} className="text-center mb-10 reveal">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-[hsl(237,52%,50%)] mb-3 px-3 py-1 rounded-full bg-[hsl(237,52%,50%)]/10">
            FAQ
          </span>
          <h2 className="font-sans text-3xl sm:text-4xl text-foreground mb-3">
            Questions parents and students ask
          </h2>
          <p className="text-muted-foreground text-sm max-w-md mx-auto">
            Everything you need to know before booking your first session.
          </p>
        </div>

        {/* Search */}
        <div className="max-w-md mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder="Search questions…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[hsl(237,52%,50%)]/30 focus:border-[hsl(237,52%,50%)]/50 transition"
            />
          </div>
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => { setActiveCategory(cat.id); setSearch(""); }}
              className={cn(
                "inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold border transition-all duration-200",
                activeCategory === cat.id
                  ? "bg-[hsl(237,52%,50%)] text-white border-[hsl(237,52%,50%)] shadow-sm"
                  : "bg-card text-muted-foreground border-border hover:border-[hsl(237,52%,50%)]/40 hover:text-foreground"
              )}
            >
              {cat.icon}
              {cat.label}
            </button>
          ))}
        </div>

        {/* FAQ grid */}
        <div ref={listRef} className="max-w-5xl mx-auto reveal">
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground text-sm">
              No questions match your search.{" "}
              <button
                className="text-[hsl(237,52%,50%)] font-semibold hover:underline underline-offset-4"
                onClick={() => { setSearch(""); setActiveCategory("all"); }}
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="space-y-3">
                {left.map((item) => {
                  const globalIndex = filtered.indexOf(item);
                  return (
                    <FAQItem
                      key={item.q}
                      q={item.q}
                      a={item.a}
                      isOpen={openIndex === globalIndex}
                      onToggle={() => setOpenIndex(openIndex === globalIndex ? null : globalIndex)}
                    />
                  );
                })}
              </div>
              <div className="space-y-3">
                {right.map((item) => {
                  const globalIndex = filtered.indexOf(item);
                  return (
                    <FAQItem
                      key={item.q}
                      q={item.q}
                      a={item.a}
                      isOpen={openIndex === globalIndex}
                      onToggle={() => setOpenIndex(openIndex === globalIndex ? null : globalIndex)}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer CTA */}
        <div className="text-center mt-12">
          <p className="text-sm text-muted-foreground">
            Still have questions?{" "}
            <Link to="/contact" className="text-[hsl(237,52%,50%)] font-semibold hover:underline underline-offset-4">
              Contact us
            </Link>
          </p>
        </div>

      </div>
    </section>
  );
}
